import { diffArrays, diffWords, type ArrayChange } from 'diff';

export type DiffTokenType = 'equal' | 'added' | 'removed';

export type DiffToken = {
  text: string;
  type: DiffTokenType;
};

export type DiffOptions = {
  /** Игнорировать регистр при сравнении. */
  ignoreCase?: boolean;
  /** Игнорировать различия в пробелах. */
  ignoreWhitespace?: boolean;
};

const toType = (change: { added?: boolean; removed?: boolean }): DiffTokenType => {
  if (change.added) return 'added';
  if (change.removed) return 'removed';
  return 'equal';
};

/**
 * Сегментация текста на слова и пробелы через Intl.Segmenter.
 * granularity 'word' возвращает слова и пробельные сегменты по отдельности,
 * что позволяет считать пробелы значимыми токенами (поведение WordsWithSpace),
 * сохраняя при этом корректную токенизацию кириллицы/Unicode.
 */
const segmentWithSpaces = (text: string, segmenter: Intl.Segmenter): string[] =>
  Array.from(segmenter.segment(text), (s) => s.segment);

/**
 * Пословное сравнение двух текстов с поддержкой Unicode/кириллицы.
 *
 * Различие веток обязательно для UI-тумблера «Игнорировать пробелы»:
 * - ignoreWhitespace=false (дефолт): пробелы значимы. diffWordsWithSpace из diff@7
 *   НЕ применяет intlSegmenter к словам и режет кириллицу по символам, поэтому
 *   собираем эквивалент через diffArrays поверх сегментов Intl.Segmenter —
 *   пробелы остаются отдельными токенами и видны как изменения.
 * - ignoreWhitespace=true: diffWords нормализует пробелы, различия в них гасятся.
 *
 * intlSegmenter создаётся один раз и переиспользуется в обеих ветках.
 */
export const computeDiff = (
  oldText: string,
  newText: string,
  options: DiffOptions = {}
): DiffToken[] => {
  const { ignoreCase = false, ignoreWhitespace = false } = options;
  const intlSegmenter = new Intl.Segmenter(undefined, { granularity: 'word' });

  if (ignoreWhitespace) {
    const changes = diffWords(oldText, newText, { ignoreCase, intlSegmenter });
    return changes
      .filter((c) => c.value.length > 0)
      .map((c) => ({ text: c.value, type: toType(c) }));
  }

  const compare = ignoreCase
    ? (a: string, b: string): boolean => a.toLowerCase() === b.toLowerCase()
    : (a: string, b: string): boolean => a === b;

  const changes: ArrayChange<string>[] = diffArrays(
    segmentWithSpaces(oldText, intlSegmenter),
    segmentWithSpaces(newText, intlSegmenter),
    { comparator: compare }
  );

  return changes
    .map((c) => ({ text: c.value.join(''), type: toType(c) }))
    .filter((t) => t.text.length > 0);
};

/** Количество добавленных / удалённых слов (без учёта чисто пробельных токенов). */
export const countChanges = (tokens: DiffToken[]): { added: number; removed: number } => {
  const wordCount = (text: string): number =>
    text.split(/\s+/).filter((w) => w.trim().length > 0).length;

  return tokens.reduce(
    (acc, t) => {
      if (t.type === 'added') acc.added += wordCount(t.text);
      if (t.type === 'removed') acc.removed += wordCount(t.text);
      return acc;
    },
    { added: 0, removed: 0 }
  );
};
